import { useState, useRef } from "react";
import { api, euro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, Sparkle, Check, X, ArrowClockwise, Warning, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const inpCls = "h-11 rounded-lg border-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200";

export default function ReceiptScanDialog({ categories, onClose, onSaved }) {
  const [step, setStep] = useState("upload"); // upload | analyzing | review
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [confidence, setConfidence] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const galleryRef = useRef(null);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    // PDF: kein Bild-Preview verfügbar, zeige stattdessen Datei-Info
    if (f.type === "application/pdf") setPreview("pdf");
    else setPreview(URL.createObjectURL(f));
    setStep("analyzing");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/expenses/analyze-receipt", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      const ex = r.data.extracted || {};
      setExtracted({
        date:  ex.date || new Date().toISOString().slice(0,10),
        vendor: ex.vendor || "",
        purpose: ex.purpose || (ex.vendor ? `Einkauf ${ex.vendor}` : ""),
        amount: ex.total || 0,
        category: ex.category || "Sonstiges",
        mwst_7_amount: ex.mwst_7_amount || 0,
        mwst_19_amount: ex.mwst_19_amount || 0,
        mwst_7_net: ex.mwst_7_net || 0,
        mwst_19_net: ex.mwst_19_net || 0,
        haus_geteilt: false,
        notes: ex.notes || "",
      });
      setConfidence(ex.confidence || 0);
      setStep("review");
    } catch (err) {
      toast.error(err.response?.data?.detail || "KI-Scan fehlgeschlagen");
      setStep("upload");
      setFile(null);
      setPreview(null);
    } finally { setBusy(false); }
  };

  const set = (k, v) => setExtracted(x => ({ ...x, [k]: v }));

  const save = async () => {
    if (!extracted.date || !extracted.purpose || !extracted.amount) {
      toast.error("Bitte Datum, Zweck und Betrag prüfen."); return;
    }
    setBusy(true);
    try {
      const payload = {
        date: extracted.date,
        purpose: extracted.purpose,
        amount: parseFloat(extracted.amount),
        category: extracted.category || "Sonstiges",
        notes: extracted.notes || "",
        vendor: extracted.vendor || "",
        haus_geteilt: !!extracted.haus_geteilt,
        mwst_7_amount:  parseFloat(extracted.mwst_7_amount) || 0,
        mwst_19_amount: parseFloat(extracted.mwst_19_amount) || 0,
        mwst_7_net:     parseFloat(extracted.mwst_7_net) || 0,
        mwst_19_net:    parseFloat(extracted.mwst_19_net) || 0,
      };
      const saved = (await api.post("/expenses", payload)).data;
      // Beleg-Bild auch als Anhang speichern
      if (file && saved.id) {
        const fd = new FormData();
        fd.append("file", file);
        try { await api.post(`/expenses/${saved.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" }}); }
        catch (e) { /* not critical */ }
      }
      toast.success("Ausgabe gespeichert · Beleg angehängt");
      onSaved && onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const reset = () => {
    setFile(null); setPreview(null); setExtracted(null);
    setStep("upload"); if (inputRef.current) inputRef.current.value = "";
  };

  const totalCheck = extracted ? Math.abs((parseFloat(extracted.mwst_7_net||0) + parseFloat(extracted.mwst_7_amount||0) + parseFloat(extracted.mwst_19_net||0) + parseFloat(extracted.mwst_19_amount||0)) - parseFloat(extracted.amount||0)) : 0;
  const mwstMismatch = extracted && (parseFloat(extracted.mwst_7_amount||0) > 0 || parseFloat(extracted.mwst_19_amount||0) > 0) && totalCheck > 0.05;

  return (
    <Dialog open={true} onOpenChange={(o)=>{ if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="receipt-scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkle size={20} weight="duotone" className="text-primary"/>
            KI-Beleg-Scan
          </DialogTitle>
        </DialogHeader>

        {step === "upload" && (
          <div className="text-center py-6" data-testid="scan-step-upload">
            <div className="mx-auto w-20 h-20 rounded-2xl bg-indigo-100 flex items-center justify-center mb-4">
              <Camera size={36} weight="duotone" className="text-indigo-700"/>
            </div>
            <h3 className="font-bold text-lg mb-1">Beleg oder Kassenzettel fotografieren</h3>
            <p className="text-sm text-muted-foreground mb-4">Die KI liest Datum, Betrag, Händler und Mehrwertsteuer automatisch aus.</p>
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={onFile} className="hidden" data-testid="scan-file-input"/>
            <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={onFile} className="hidden" data-testid="scan-gallery-input"/>
            <div className="flex flex-wrap justify-center gap-2">
              <Button onClick={pick} className="rounded-lg bg-primary text-white h-11 px-6" data-testid="scan-pick-btn">
                <Camera size={16} weight="bold"/><span className="ml-2 font-semibold">Kamera öffnen</span>
              </Button>
              <Button onClick={()=>galleryRef.current?.click()} variant="outline" className="rounded-lg h-11 px-6" data-testid="scan-gallery-btn">
                <span className="font-semibold">Foto oder PDF wählen</span>
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">JPG / PNG / WebP · <strong>PDF (Lieferanten-Rechnungen)</strong> · max 10 MB · KI: GPT-5.4 Vision</p>
          </div>
        )}

        {step === "analyzing" && (
          <div className="text-center py-8" data-testid="scan-step-analyzing">
            <div className="mx-auto w-16 h-16 rounded-full border-4 border-primary border-t-transparent animate-spin mb-4"/>
            <h3 className="font-bold text-lg">KI analysiert Beleg …</h3>
            <p className="text-sm text-muted-foreground mt-1">Datum · Händler · Betrag · Mehrwertsteuer</p>
            {preview && <img src={preview} alt="preview" className="mx-auto mt-4 max-h-40 rounded-lg border border-slate-200 opacity-70"/>}
          </div>
        )}

        {step === "review" && extracted && (
          <div className="space-y-4" data-testid="scan-step-review">
            <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4">
              {preview && (
                <div>
                  {preview === "pdf" ? (
                    <div className="w-full h-40 rounded-lg border border-slate-200 bg-red-50 flex flex-col items-center justify-center text-red-700 gap-2">
                      <FileText size={40} weight="duotone"/>
                      <div className="text-xs font-semibold">PDF-Rechnung</div>
                      <div className="text-[10px] text-slate-600 px-2 text-center truncate max-w-full">{file?.name}</div>
                    </div>
                  ) : (
                    <img src={preview} alt="Beleg" className="w-full rounded-lg border border-slate-200 max-h-72 object-contain"/>
                  )}
                  <div className="mt-2 flex items-center justify-between text-[11px]">
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${confidence >= 0.7 ? "bg-emerald-100 text-emerald-800" : confidence >= 0.4 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>
                      KI-Sicherheit: {Math.round(confidence*100)}%
                    </span>
                    <button onClick={reset} className="text-primary hover:underline flex items-center gap-1" data-testid="scan-reset">
                      <ArrowClockwise size={11} weight="bold"/> Neu
                    </button>
                  </div>
                </div>
              )}
              <div className="space-y-2.5">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Datum *</Label>
                    <Input type="date" value={extracted.date} onChange={(e)=>set("date", e.target.value)} className={`${inpCls} mt-1`} data-testid="scan-date"/>
                  </div>
                  <div>
                    <Label className="text-xs">Händler</Label>
                    <Input value={extracted.vendor} onChange={(e)=>set("vendor", e.target.value)} className={`${inpCls} mt-1`} data-testid="scan-vendor"/>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Zweck *</Label>
                  <Input value={extracted.purpose} onChange={(e)=>set("purpose", e.target.value)} className={`${inpCls} mt-1`} data-testid="scan-purpose"/>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Gesamtbetrag * (EUR)</Label>
                    <Input type="number" step="0.01" min="0" value={extracted.amount} onChange={(e)=>set("amount", e.target.value)} className={`${inpCls} mt-1 font-bold`} data-testid="scan-amount"/>
                  </div>
                  <div>
                    <Label className="text-xs">Kategorie *</Label>
                    <Input value={extracted.category} onChange={(e)=>set("category", e.target.value)} list="scan-cat-list" className={`${inpCls} mt-1`} data-testid="scan-category"/>
                    <datalist id="scan-cat-list">{(categories || []).map(c => <option key={c} value={c}/>)}</datalist>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-3">
              <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold mb-2">Mehrwertsteuer-Aufteilung</div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="text-xs font-semibold text-emerald-800 mb-2">7 % · ermäßigt (z. B. Lebensmittel)</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px]">Netto</Label>
                      <Input type="number" step="0.01" min="0" value={extracted.mwst_7_net} onChange={(e)=>set("mwst_7_net", e.target.value)} className="h-9 rounded-md text-xs mt-0.5" data-testid="scan-7-net"/>
                    </div>
                    <div>
                      <Label className="text-[10px]">Steuer</Label>
                      <Input type="number" step="0.01" min="0" value={extracted.mwst_7_amount} onChange={(e)=>set("mwst_7_amount", e.target.value)} className="h-9 rounded-md text-xs mt-0.5" data-testid="scan-7-amount"/>
                    </div>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-sky-50 border border-sky-200">
                  <div className="text-xs font-semibold text-sky-800 mb-2">19 % · regulär (z. B. Getränke, Reparatur)</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px]">Netto</Label>
                      <Input type="number" step="0.01" min="0" value={extracted.mwst_19_net} onChange={(e)=>set("mwst_19_net", e.target.value)} className="h-9 rounded-md text-xs mt-0.5" data-testid="scan-19-net"/>
                    </div>
                    <div>
                      <Label className="text-[10px]">Steuer</Label>
                      <Input type="number" step="0.01" min="0" value={extracted.mwst_19_amount} onChange={(e)=>set("mwst_19_amount", e.target.value)} className="h-9 rounded-md text-xs mt-0.5" data-testid="scan-19-amount"/>
                    </div>
                  </div>
                </div>
              </div>
              {mwstMismatch && (
                <div className="mt-2 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="scan-mismatch">
                  <Warning size={14} weight="fill"/>
                  <span>Netto + Steuer ergibt {euro(totalCheck > 0 ? parseFloat(extracted.mwst_7_net||0)+parseFloat(extracted.mwst_7_amount||0)+parseFloat(extracted.mwst_19_net||0)+parseFloat(extracted.mwst_19_amount||0) : 0)}, weicht {euro(totalCheck)} vom Gesamtbetrag ab — bitte prüfen.</span>
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs">Notiz (optional)</Label>
              <Textarea value={extracted.notes} onChange={(e)=>set("notes", e.target.value)} rows={2} className="rounded-lg border-slate-200 mt-1 text-sm"/>
            </div>

            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg bg-amber-50 border border-amber-200 hover:bg-amber-100 transition" data-testid="scan-haus-geteilt-label">
              <input
                type="checkbox"
                checked={!!extracted.haus_geteilt}
                onChange={(e)=>set("haus_geteilt", e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-amber-600"
                data-testid="scan-haus-geteilt"
              />
              <div>
                <div className="font-semibold text-sm text-amber-900">2/3 anteilig (Haus-Gemeinkosten)</div>
                <div className="text-[11px] text-amber-800 leading-snug">Für Rechnungen, die das gesamte Haus betreffen (Dach, Heizung, Allgemein-Strom). Nur 2 von 3 Wohnungen sind vermietet → 2/3 absetzbar.</div>
              </div>
            </label>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-lg">
                <X size={14} weight="bold"/><span className="ml-1.5">Abbrechen</span>
              </Button>
              <Button onClick={save} disabled={busy} className="rounded-lg bg-primary text-white" data-testid="scan-confirm">
                <Check size={14} weight="bold"/><span className="ml-1.5 font-semibold">Bestätigen & speichern</span>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
